import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { getCreateActor, getSettings, sendActorEvent } from '@/queries';
import useAppStore from '@/store/app';
import useFlowStore from '@/store/flow';
import type {
	TGroupItem,
	TGroupItemFrontend,
	TLibraryItemFrontend
} from '@/types/common';
import type { TAccount } from '@/types/common.ts';
import { ActorCommands } from '@/types/global-events.types.ts';
import { editorValueToMarkdown } from '@/utils/editor-conversion';

const useFlow = () => {
	const connectorData = useAppStore((state) => state.connectorData);
	const setConnectorData = useAppStore((state) => state.setConnectorData);
	const { deleteItem, addItem } = useFlowStore(
		useShallow((state) => ({
			deleteItem: state.deleteItem,
			addItem: state.addItem
		}))
	);
	const eventPayload = useMemo(() => {
		if (
			connectorData.client_secret &&
			connectorData.external_id &&
			connectorData.idn
		) {
			return {
				external_id: connectorData.external_id,
				connector_idn: connectorData.idn,
				client_secret: connectorData.client_secret
			};
		}
		return undefined;
	}, [connectorData]);

	const initApp = useCallback(
		(clientSecret: string, account: TAccount) => {
			getSettings({ client_secret: clientSecret }).then(async (settings) => {
				await getCreateActor({
					name: `Workflow builder actor: ${account.name === account.email ? account.email : account.name}`,
					external_id: account.id,
					connector_idn: settings.connector_idn,
					client_secret: settings.client_secret
				});
				setConnectorData({
					idn: settings.connector_idn,
					client_secret: settings.client_secret,
					external_id: account.id
				});
			});
		},
		[setConnectorData]
	);

	const initCanvas = useCallback(() => {
		if (eventPayload) {
			sendActorEvent({
				...eventPayload,
				event_idn: ActorCommands.INIT_CANVAS,
				arguments: []
			});
		}
	}, [eventPayload]);

	const initLibrary = useCallback(() => {
		if (eventPayload) {
			sendActorEvent({
				...eventPayload,
				event_idn: ActorCommands.INIT_LIBRARY,
				arguments: []
			});
		}
	}, [eventPayload]);

	const toBackendGroupItem = useCallback(
		(item: TGroupItemFrontend): TGroupItem => {
			return {
				...item,
				title: editorValueToMarkdown(item.title),
				body: editorValueToMarkdown(item.body)
			};
		},
		[]
	);

	const saveItem = useCallback(
		(item: TGroupItemFrontend, typeIdn: string) => {
			if (eventPayload) {
				const backendItem = toBackendGroupItem(item);

				sendActorEvent({
					...eventPayload,
					event_idn: ActorCommands.SAVE_ITEM,
					arguments: [
						{ name: 'body', value: JSON.stringify(backendItem) },
						{ name: 'type_idn', value: typeIdn }
					]
				});
			}
		},
		[eventPayload, toBackendGroupItem]
	);

	const deleteCanvasItem = useCallback(
		async (typeIdn: string, itemIdn: string) => {
			if (eventPayload) {
				await sendActorEvent({
					...eventPayload,
					event_idn: ActorCommands.DELETE_ITEM,
					arguments: [
						{ name: 'item_idn', value: itemIdn },
						{ name: 'type_idn', value: typeIdn }
					]
				});
				deleteItem(typeIdn, itemIdn);
			}
		},
		[eventPayload, deleteItem]
	);

	const duplicateLibraryAsEditable = useCallback(
		async (item: TGroupItemFrontend, typeIdn: string) => {
			if (eventPayload) {
				await deleteCanvasItem(typeIdn, item.origin_idn as string);
				saveItem(item, typeIdn);
				addItem(typeIdn, item);
			}
		},
		[eventPayload, deleteCanvasItem, saveItem, addItem]
	);

	const revertToOriginal = useCallback(
		async (
			item: TGroupItemFrontend,
			typeIdn: string,
			original: TLibraryItemFrontend
		) => {
			if (eventPayload && item.origin_idn) {
				await deleteCanvasItem(typeIdn, item.idn);
				const payload: TGroupItemFrontend = {
					...original,
					idn: item.origin_idn,
					is_from_library: true,
					origin_idn: ''
				};
				saveItem(payload, typeIdn);
				addItem(typeIdn, payload);
			}
		},
		[eventPayload, saveItem, deleteCanvasItem, addItem]
	);

	const reorderCanvasItems = useCallback(
		async () =>
			new Promise((resolve, reject) => {
				if (eventPayload) {
					const { canvas: currentCanvas } = useFlowStore.getState();
					const types = Object.keys(currentCanvas.configs).map((typeIdn) => ({
						...currentCanvas.configs[typeIdn],
						items: (currentCanvas.sections[typeIdn] ?? []).map(
							toBackendGroupItem
						)
					}));
					const body = JSON.stringify(types);
					sendActorEvent({
						...eventPayload,
						event_idn: ActorCommands.SAVE_CANVAS,
						arguments: [{ name: 'body', value: body }]
					}).then(resolve);
				} else {
					reject(new Error('Event payload is not defined'));
				}
			}),
		[eventPayload, toBackendGroupItem]
	);

	return {
		initApp,
		initCanvas,
		initLibrary,
		saveItem,
		duplicateLibraryAsEditable,
		revertToOriginal,
		deleteCanvasItem,
		reorderCanvasItems
	};
};

export default useFlow;
